import {
  type AmountCurrencyRequest,
  type AmountCurrencyResponse,
  type CashierPaymentMethod,
  type CurrencyPrimitive,
  EnumActionType,
  type FormFields,
  type InitSessionTokenResponse,
  type TradingAccountRefundBalance,
} from "@/api/cashier";
import i18n from "@/i18n/config";
import type {
  AmountsByMethodId,
  CurrencyConversion,
  CurrencyConversionBySpaAndCode,
  RefundContext,
} from "@/pages/withdrawals/types";
import { DEFAULT_ID } from "@/utils/constants";
import type { ReactNode } from "react";
import Decimal from "decimal.js";

export const getPaymentMethodFormFields = (
  selectedPaymentMethod: CashierPaymentMethod | undefined,
  licenseId: string | undefined
): FormFields | undefined => {
  return licenseId
    ? (selectedPaymentMethod?.form[licenseId] ??
        selectedPaymentMethod?.form[DEFAULT_ID])
    : undefined;
};

export const isCardForm = (paymentMethod?: CashierPaymentMethod) => {
  return paymentMethod?.action_type === EnumActionType.EnumActionTypeCard;
};

export const isGooglePay = (paymentMethod?: CashierPaymentMethod) => {
  return (
    paymentMethod?.action_type === EnumActionType.EnumActionTypeApm &&
    paymentMethod?.id === "googlepay"
  );
};

export const isApplePay = (paymentMethod?: CashierPaymentMethod) => {
  return (
    paymentMethod?.action_type === EnumActionType.EnumActionTypeApm &&
    paymentMethod?.id === "applepay"
  );
};

export const isPayPal = (paymentMethod?: CashierPaymentMethod) => {
  if (
    !paymentMethod ||
    paymentMethod?.action_type !== EnumActionType.EnumActionTypeApm
  ) {
    return false;
  }
  const id = paymentMethod.id?.toLowerCase?.() ?? "";
  return id === "paypal";
};

export const isApm = (paymentMethod?: CashierPaymentMethod) => {
  return paymentMethod?.action_type === EnumActionType.EnumActionTypeApm;
};

export const isBankTransfer = (paymentMethod?: CashierPaymentMethod) => {
  return (
    paymentMethod?.action_type === EnumActionType.EnumActionTypeBankTransfer
  );
};

export const getAssetUrl = (assetName: string, folder?: string) => {
  const assetPath = import.meta.env.VITE_ASSET_PATH ?? "/";
  const basePath = assetPath.endsWith("/") ? assetPath : `${assetPath}/`;
  return folder
    ? `${basePath}${folder}/${assetName}`
    : `${basePath}${assetName}`;
};

export type SanitizeResult = {
  sanitized: string; // safe to render in input (may end with ".")
  numeric: string; // safe to parse as number ("" if empty/invalid)
  hasTrailingDot: boolean;
};

/**
 * Sanitize numeric input for amount fields.
 * - Accepts digits and the locale's decimal separator ("." or ",")
 * - Treats the other separator char as a group separator and strips it
 * - Strips everything else
 * - Clamps decimals to currency.decimals
 * - Removes leading zeros (keeps "0.x")
 * - Preserves trailing decimal separator while user is typing
 * - Always returns `sanitized`/`numeric` using "." so downstream parseFloat works
 */
export const sanitizeNumberInput = (
  value: string,
  currency: CurrencyPrimitive | undefined,
  locale?: string
): SanitizeResult => {
  const decimals = currency?.decimals ?? 2;

  // Detect decimal separator for the active locale (fallback ".")
  const decimalSep = getDecimalSeparator(locale ?? i18n.language);
  const groupSep = decimalSep === "," ? "." : ",";

  // Strip group separators, then whitelist digits + decimal sep,
  // then normalize decimal sep to "."
  let input = value
    .split(groupSep)
    .join("")
    .replace(new RegExp(`[^0-9${decimalSep === "." ? "\\." : ","}]`, "g"), "")
    .replace(decimalSep, ".")
    .replace(/[^0-9.]/g, ""); // strip any residual separators after normalization

  const hasDot = input.includes(".");

  // Collapse multiple dots — keep first
  const firstDot = input.indexOf(".");
  if (firstDot !== -1) {
    input =
      input.slice(0, firstDot + 1) +
      input.slice(firstDot + 1).replace(/\./g, "");
  }

  if (input === "") {
    return { sanitized: "", numeric: "", hasTrailingDot: false };
  }

  const [intPart = "", decPart = ""] = input.split(".");

  // Strip leading zeros: "007" -> "7", but keep "0" and "0.x"
  const cleanInt = intPart.replace(/^0+(?=\d)/, "") || "0";

  if (decimals === 0)
    return { sanitized: cleanInt, numeric: cleanInt, hasTrailingDot: false };

  // Clamp decimals
  const cleanDec = decPart.slice(0, decimals);

  const sanitized = hasDot ? `${cleanInt}.${cleanDec}` : cleanInt;
  const numeric = hasDot && cleanDec === "" ? cleanInt : sanitized;

  return {
    sanitized,
    numeric,
    hasTrailingDot: hasDot && cleanDec === "",
  };
};

/**
 * Parses a string and returns an array of strings and objects representing bolded words.
 * Bolded words are denoted by double asterisks (**) in the input string.
 * @param text - The input string may containing words to be bolded.
 * @returns An array where normal text segments are strings and bolded words are objects with a `bold` property.
 */
export const getBoldedWords = (text: string) => {
  const regex = /\*\*([^*]+)\*\*/g; // Match text between ** pairs
  const matches: (string | { bold: string })[] = [];

  text.split(" ").forEach((part) => {
    const match = regex.exec(part);
    if (match) {
      matches.push({ bold: match[1] });
    } else {
      matches.push(part);
    }
    regex.lastIndex = 0;
  });

  return matches;
};

type MerchantConfig = Pick<
  InitSessionTokenResponse,
  "env" | "merchant_id" | "merchant_site_id"
>;

export const getMerchantConfigs = ({
  env,
  merchant_id,
  merchant_site_id,
}: MerchantConfig) => {
  return {
    env: env ?? (import.meta.env.VITE_NUVEI_ENV as string),
    merchantId:
      merchant_id ?? (import.meta.env.VITE_NUVEI_MERCHANT_ID as string),
    merchantSiteId:
      merchant_site_id ??
      (import.meta.env.VITE_NUVEI_MERCHANT_SITE_ID as string),
  };
};

/**
 * Loads a script from the given URL.
 * @param src - The URL of the script to load.
 * @param options - Optional settings. Set `crossorigin: true` to add the crossorigin attribute.
 * @returns A promise that resolves when the script is loaded.
 */
export const loadScript = (src: string, options?: { crossorigin?: boolean }) =>
  new Promise<Event>((resolve, reject) => {
    const element = document.createElement("script");
    if (options?.crossorigin) {
      element.setAttribute("crossorigin", "");
    }
    element.addEventListener("load", resolve, { once: true });
    element.addEventListener(
      "error",
      () => reject(new Error(`Failed to load script: ${src}`)),
      { once: true }
    );
    element.src = src;
    document.body.appendChild(element);
  });

/**
 * Parses a duration string and returns duration in minutes.
 * @param duration - The input string representing a duration(e.g., "2h30m").
 * @returns The duration in minutes.
 */
export const parseDuration = (duration: string): number => {
  const hoursMatch = duration.match(/(\d+)h/);
  const minutesMatch = duration.match(/(\d+)m/);
  const hours = hoursMatch ? Number.parseInt(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? Number.parseInt(minutesMatch[1]) : 0;
  return hours * 60 + minutes;
};

/**
 * Formats a Date object to a 12-hour time string.
 * @param date - The Date object to format.
 * @returns A formatted time string in 12-hour format (e.g., "9am", "5pm").
 */
export const formatTime = (
  date: Date,
  locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
): string => {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const isAmountCurrencyResponse = (
  value: AmountCurrencyResponse | ReactNode
): value is AmountCurrencyResponse => {
  return (
    value !== null &&
    typeof value === "object" &&
    "amount" in value &&
    "code" in value &&
    "decimals" in value
  );
};

export const decimalToAmountCurrencyRequest = (
  amount: Decimal,
  currency: CurrencyPrimitive | undefined
): AmountCurrencyRequest => {
  if (!currency) {
    return {
      amount: "0",
      code: "",
    };
  }

  return {
    amount: decimalMinorUnits(amount, currency.decimals).toString(),
    code: currency.code,
  };
};

export const majorToAmountCurrencyRequest = (
  amount: number,
  currency: CurrencyPrimitive | undefined
): AmountCurrencyRequest => {
  if (!currency) {
    return {
      amount: "0",
      code: "",
    };
  }

  return {
    amount: toMinorUnits(amount, currency.decimals).toString(),
    code: currency.code,
  };
};

// Converts a major amount (e.g., dollars) to an AmountCurrencyResponse object with minor units (e.g., cents) based on the provided currency's decimals.
export const majorToAmountCurrencyResponse = (
  amount: number,
  currency: CurrencyPrimitive | undefined
): AmountCurrencyResponse => {
  if (!currency) {
    return {
      amount: "0",
      code: "",
      decimals: 0,
    };
  }

  return {
    amount: toMinorUnits(amount, currency.decimals).toString(),
    code: currency.code,
    decimals: currency.decimals,
  };
};

export const decimalToAmountCurrencyResponse = (
  amount: Decimal,
  currency: CurrencyPrimitive | undefined
): AmountCurrencyResponse => {
  if (!currency) {
    return {
      amount: "0",
      code: "",
      decimals: 0,
    };
  }

  return {
    amount: decimalMinorUnits(amount, currency.decimals).toString(),
    code: currency.code,
    decimals: currency.decimals,
  };
};

export const moneyToDecimal = (
  money: AmountCurrencyResponse | undefined | null
): Decimal | null => {
  if (!money) {
    return null;
  }

  const d = new Decimal(money.amount).div(new Decimal(10).pow(money.decimals));
  return d;
};

// Converts an AmountCurrencyResponse object to a number in major units (e.g., dollars from cents).
export const moneyToMajorUnits = (
  money: AmountCurrencyResponse | undefined | null
): number | null => {
  if (!money) {
    return null;
  }

  return toMajorUnits(Number.parseInt(money.amount, 10), money.decimals);
};

export const decimalMinorUnits = (
  amount: Decimal,
  decimals: number
): Decimal => {
  return amount.mul(new Decimal(10).pow(decimals)).floor(); // floor to remove any fractional amount
};

// Converts a number in major units to minor units based on the number of decimals (e.g., dollars to cents).
export const toMinorUnits = (majorAmount: number, decimals: number): number =>
  new Decimal(majorAmount)
    .mul(Decimal.pow(10, decimals))
    .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
    .toNumber();

// Converts a number in minor units to major units based on the number of decimals (e.g., cents to dollars).
export const toMajorUnits = (minorAmount: number, decimals: number): number =>
  new Decimal(minorAmount)
    .div(Decimal.pow(10, decimals))
    .toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP)
    .toNumber();

export function calculateRefundAmountLeft(
  refundAccsMap: { [k: string]: FlattenedRefundAccount },
  balance: TradingAccountRefundBalance | undefined,
  refundLeft: Decimal,
  refundAmounts: AmountsByMethodId
): Decimal {
  if (!balance) {
    return new Decimal(0);
  }

  // Round each converted leg to the trading currency's own precision, matching
  // how the backend books each per-ledger conversion (money.NewFromDecimal
  // rounds to the target currency's decimals). A hardcoded 2 dp over-counted
  // the consumed amount for 0-decimal currencies (e.g. JPY): the backend books
  // ¥100 for a leg while the FE booked ¥100.50, so maxing every refund pushed
  // the remainder to a sub-unit negative that rendered as "-0" and wrongly
  // tripped the over-allocation guard (PNX-5760).
  const tradingDecimals = balance.refundable_money.decimals ?? 2;

  const amount: Decimal = Object.entries(refundAmounts).reduce<Decimal>(
    (acc, [k, v]) => {
      if (v === undefined) {
        return acc;
      }
      const entry = refundAccsMap[k];
      if (!entry) return acc;
      const code = k.split("_")[1];
      const ledgerId = entry.ledger_id;
      const ratio = getRefundRatio(balance, ledgerId, code);

      return acc.add(
        v ? v.mul(ratio).toDecimalPlaces(tradingDecimals) : new Decimal(0)
      );
    },
    new Decimal(0)
  );
  const diff =
    refundLeft?.sub(amount).toDecimalPlaces(tradingDecimals) || new Decimal(0);

  return diff.isZero() ? new Decimal(0) : diff;
}

export function getRefundRatio(
  balance: TradingAccountRefundBalance | undefined,
  ledgerId: string,
  code: string
): Decimal {
  let rate =
    balance?.currency_conversion_by_asset?.[ledgerId]?.[code]?.rate ?? "1";

  if (rate === "0") rate = "1";

  return new Decimal(rate);
}

/**
 * Calculates the maximum refund amounts per ledger and payment method,
 * taking into account already-allocated refund amounts across shared ledgers.
 *
 * When multiple saved payment accounts (SPAs) share the same ledger, the max
 * refund for each SPA must be reduced by what other SPAs in that ledger have
 * already allocated.
 *
 * @returns A nested map of { spaId -> currencyCode -> { ledgerAmount, tradingAmount } },
 *          or null if no balance is available.
 */
export const calcMaxRefunds = (
  refundAccsMap: { [k: string]: FlattenedRefundAccount },
  balance: TradingAccountRefundBalance | undefined,
  refundCtx: RefundContext
): CurrencyConversionBySpaAndCode | null => {
  if (!balance) {
    return null;
  }

  /**
   * Tracks already-allocated ledger/trading amounts for SPAs that don't yet
   * have a refund amount set. Used in a second pass to adjust their maximums.
   *
   * Shape: { spaId -> currencyCode -> CurrencyConversion }
   */
  const pendingAdjustments: CurrencyConversionBySpaAndCode = {};

  const maxRefunds = Object.entries(
    refundAccsMap
  ).reduce<CurrencyConversionBySpaAndCode>((acc, [_, spa]) => {
    if (spa === undefined) {
      return acc;
    }

    const { ledger_id, minimum_amount } = spa;
    const spaId = spa.id ?? spa.payment_method_id;

    const currencyCode = minimum_amount.code;

    const currencyConversion =
      balance?.currency_conversion_by_asset?.[ledger_id]?.[currencyCode];
    const baseLedgerMax =
      moneyToDecimal(currencyConversion?.ledger_money) ?? undefined;
    const baseTradingMax =
      moneyToDecimal(currencyConversion?.trading_money) ?? new Decimal(0);

    let ledgerMax = baseLedgerMax;
    let tradingMax = baseTradingMax;
    const refundAmount = refundCtx.refundAmounts[`${spaId}_${currencyCode}`];

    const isMultiCurrency = baseLedgerMax !== undefined;
    const refundRatio = isMultiCurrency
      ? getRefundRatio(balance, ledger_id, currencyCode)
      : undefined;

    const isInputAdded = refundAmount !== undefined;
    if (isInputAdded) {
      const result = applyAllocatedRefund(
        spaId,
        ledger_id,
        currencyCode,
        refundAmount,
        refundCtx,
        pendingAdjustments,
        refundRatio
      );
      if (result) {
        tradingMax = result.tradingMax;
        if (result.ledgerMax !== undefined) ledgerMax = result.ledgerMax;
      }
    } else if (tradingMax.gt(refundCtx.refundAmountLeft)) {
      tradingMax = refundCtx.refundAmountLeft;
      if (isMultiCurrency && refundRatio !== undefined)
        ledgerMax = refundCtx.refundAmountLeft
          .div(refundRatio)
          .toDecimalPlaces(2, Decimal.ROUND_FLOOR);
    }

    acc[spaId] ??= {};

    acc[spaId][currencyCode] = {
      tradingAmount: tradingMax,
      ledgerAmount: ledgerMax,
      ratio: refundRatio ?? new Decimal(1),
    } as CurrencyConversion;

    return acc;
  }, {} as CurrencyConversionBySpaAndCode);

  applyPendingAdjustments(maxRefunds, pendingAdjustments, refundCtx);

  return maxRefunds;
};

function applyAllocatedRefund(
  spaId: string,
  ledger_id: string,
  currencyCode: string,
  refundAmount: Decimal,
  refundCtx: RefundContext,
  pendingAdjustments: CurrencyConversionBySpaAndCode,
  refundRatio: Decimal | undefined
): { tradingMax: Decimal; ledgerMax: Decimal | undefined } | undefined {
  const initMax = refundCtx.initMaxRefunds?.[spaId]?.[currencyCode];
  if (initMax === undefined) return undefined;

  let allocatedLedger: Decimal | undefined;
  let allocatedTrading: Decimal = refundAmount;
  // for multi currency refundAmount is in ledger currency
  if (refundRatio !== undefined) {
    allocatedLedger = refundAmount;
    allocatedTrading = refundAmount.mul(refundRatio).toDecimalPlaces(2);
  }

  let otherSPALedgerAmount = new Decimal(0);
  let otherSPATradingAmount = new Decimal(0);
  const spaWithoutAllocation: string[] = [];

  // collect the amounts allocated by sibling SPAs sharing the same ledger, to adjust the current SPA's max accordingly.
  // If siblings don't have an allocation yet, track them for a second pass adjustment.
  for (const siblingId of refundCtx.refundMethodsByLedger[ledger_id]) {
    if (siblingId === spaId) continue;

    const siblingAmount =
      refundCtx.refundAmounts[`${siblingId}_${currencyCode}`];
    if (siblingAmount !== undefined) {
      if (refundRatio !== undefined) {
        otherSPATradingAmount = otherSPATradingAmount.add(
          siblingAmount.mul(refundRatio).toDecimalPlaces(2)
        );
        otherSPALedgerAmount = otherSPALedgerAmount.add(siblingAmount);
      } else {
        otherSPATradingAmount = otherSPATradingAmount.add(siblingAmount);
      }
    } else {
      pendingAdjustments[siblingId] ??= {};
      spaWithoutAllocation.push(siblingId);
    }
  }

  let tradingMax = initMax.tradingAmount;
  let ledgerMax = initMax.ledgerAmount;

  let pendingLedgerAdjustment = new Decimal(0);
  let pendingTradingAdjustment = new Decimal(0);
  // if we were bound by the init refund amount we need to handle it differently
  if (tradingMax.eq(refundCtx.initRefundAmountLeft)) {
    // add back the allocated amounts so max doesn't reduce
    tradingMax = refundCtx.refundAmountLeft.add(allocatedTrading);
    if (refundRatio !== undefined && allocatedLedger !== undefined) {
      ledgerMax = refundCtx.refundAmountLeft
        .div(refundRatio)
        .add(allocatedLedger)
        .toDecimalPlaces(2, Decimal.ROUND_FLOOR);
    }
  } else {
    // if refund amount left is 0; and customer entered 0 we should just set max to 0
    if (refundCtx.refundAmountLeft.isZero() && allocatedTrading.isZero()) {
      tradingMax = new Decimal(0);
      if (ledgerMax !== undefined) {
        ledgerMax = new Decimal(0);
      }
      return { ledgerMax, tradingMax };
    }

    // adjust max by what siblings have allocated
    ledgerMax =
      ledgerMax !== undefined
        ? Decimal.max(0, ledgerMax.sub(otherSPALedgerAmount))
        : undefined;
    tradingMax = Decimal.max(0, tradingMax.sub(otherSPATradingAmount));

    // Cap at what's reachable: remaining global budget + own allocation.
    // Handles the case where other SPAs (on different ledgers) have consumed
    // refundAmountLeft, leaving this SPA's max above what's actually enterable.
    const effectiveTradingCap =
      refundCtx.refundAmountLeft.add(allocatedTrading);
    if (tradingMax.gt(effectiveTradingCap)) {
      tradingMax = effectiveTradingCap;
      if (refundRatio !== undefined && allocatedLedger !== undefined) {
        ledgerMax = refundCtx.refundAmountLeft
          .div(refundRatio)
          .toDecimalPlaces(2, Decimal.ROUND_FLOOR)
          .add(allocatedLedger);
      }
    }

    pendingTradingAdjustment = allocatedTrading.add(otherSPATradingAmount);
    if (allocatedLedger !== undefined)
      pendingLedgerAdjustment = allocatedLedger.add(otherSPALedgerAmount);
  }
  for (const methodId of spaWithoutAllocation) {
    pendingAdjustments[methodId][currencyCode] = {
      tradingAmount: pendingTradingAdjustment,
      ratio: refundRatio ?? new Decimal(1),
      ...(allocatedLedger !== undefined && {
        ledgerAmount: pendingLedgerAdjustment,
      }),
    };
  }

  return { ledgerMax, tradingMax };
}

/**
 * Second pass: applies pending sibling adjustments to SPAs that had no
 * allocation when their siblings were processed in the first pass.
 */
const applyPendingAdjustments = (
  maxRefunds: CurrencyConversionBySpaAndCode,
  pendingAdjustments: CurrencyConversionBySpaAndCode,
  refundCtx: RefundContext
): void => {
  for (const [spaId, currencies] of Object.entries(pendingAdjustments)) {
    for (const [code, pendingAdjustment] of Object.entries(currencies)) {
      const currentMax = maxRefunds?.[spaId]?.[code];
      if (!currentMax) continue;

      const initMax = refundCtx.initMaxRefunds?.[spaId]?.[code];
      if (initMax === undefined) continue;

      currentMax.tradingAmount = Decimal.max(
        0,
        initMax.tradingAmount.sub(pendingAdjustment.tradingAmount)
      );

      // if after adjustment we're above the refund amount left, cap it, and adjust ledger max accordingly if it's a multi-currency scenario
      if (currentMax.tradingAmount.gt(refundCtx.refundAmountLeft)) {
        currentMax.tradingAmount = refundCtx.refundAmountLeft.toDecimalPlaces(
          2,
          Decimal.ROUND_FLOOR
        );
        if (
          initMax.ledgerAmount !== undefined ||
          pendingAdjustment.ledgerAmount !== undefined
        ) {
          currentMax.ledgerAmount = refundCtx.refundAmountLeft
            .div(pendingAdjustment.ratio)
            .toDecimalPlaces(2, Decimal.ROUND_FLOOR);
        }

        if (currentMax.tradingAmount.isZero()) {
          currentMax.ledgerAmount = currentMax.ledgerAmount
            ? new Decimal(0)
            : undefined;
        }

        continue;
      }

      if (
        initMax.ledgerAmount === undefined ||
        pendingAdjustment.ledgerAmount === undefined
      )
        continue;

      currentMax.ledgerAmount = Decimal.max(
        0,
        initMax.ledgerAmount.sub(pendingAdjustment.ledgerAmount)
      );
    }
  }
};

export type FlattenedRefundAccount = {
  id?: string | null;
  detail: string;
  secondary_detail?: string;
  name: string;
  logo_url: string;
  payment_method_id: string;
  deposit_payment_method_id?: string;
  ledger_id: string;
  minimum_amount: AmountCurrencyResponse;
  maximum_amount: AmountCurrencyResponse;
};

export const getRefundAccountsByIdAndCurrency = (
  balance: TradingAccountRefundBalance | undefined
): { [k: string]: FlattenedRefundAccount } => {
  if (!balance) return {};
  return balance.refund_accounts.reduce<{
    [k: string]: FlattenedRefundAccount;
  }>((acc, account) => {
    const { ledger_entries, ...accountFields } = account;
    for (const entry of ledger_entries) {
      const accountKey = `${account.id ?? account.payment_method_id}_${entry.minimum_amount.code}`;
      acc[accountKey] = {
        ...accountFields,
        ledger_id: entry.ledger_id,
        minimum_amount: entry.minimum_amount,
        maximum_amount: entry.maximum_amount,
      };
    }
    return acc;
  }, {});
};

export const getMaxAmount = (
  c: CurrencyConversion | undefined
): Decimal | undefined => {
  if (c === undefined) {
    return undefined;
  }

  const maxAmount = c.ledgerAmount ?? c.tradingAmount;
  return maxAmount.isZero() ? undefined : maxAmount; // if max is 0 other SPA cleared deposit;
};

export const getDecimalSeparator = (
  locale: Intl.UnicodeBCP47LocaleIdentifier
): string => {
  return (
    Intl.NumberFormat(locale)
      .formatToParts(1.1)
      .find((p) => p.type === "decimal")?.value ?? "."
  );
};
